// ── PhaseExecutionPanel ──
// Shared interactive JobSpec execution surface.
//
// Single source of truth for installer-facing execution UI. Used by:
//   • src/pages/ContractorPortal.tsx   (subcontractor route)
//   • src/pages/InstallerDashboardPage.tsx (installer route)
//
// Engine: src/data/jobSpecExecution.ts only. No new execution logic.
// Guards: assignment guard + tombstone QA rule + material-kit lock-on-canonical.

import { forwardRef, useMemo } from "react";
import { toast } from "sonner";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import { useRole } from "@/data/RoleContext";
import { getActiveQaFailReason } from "@/data/portal/contractorPortalSelectors";
import { isKitGateCleared, kitProgress } from "@/data/materialKit";
import {
  phaseProgressPct,
  recordPhoto,
  toggleChecklist,
  submitPhase,
  isPhaseUnlocked,
  isPhaseReadyToSubmit,
  ensureExecution,
  type PhaseExecution,
  type JobSpecExecution,
} from "@/data/jobSpecExecution";
import { assertMaterialAlignment } from "@/data/materialAlignmentGate";
import { useOrderDraft } from "@/data/OrderDraftContext";
import { useVendorPO } from "@/data/VendorPOContext";
import { AlignmentBlockCard } from "@/components/execution/AlignmentBlockCard";
import { assertChangeOrderHalt } from "@/data/changeOrderHalt";
import { ChangeOrderHaltCard } from "@/components/execution/ChangeOrderHaltCard";
import type {
  JobSpec,
  JobSpecPhase,
  JobSpecPhaseName,
  RequiredPhotoType,
} from "@/data/jobSpec";
import type { Job } from "@/data/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, AlertTriangle, Camera, ListChecks, Package,
} from "lucide-react";

/* ── Material Kit status pill (re-exported for callers that want it) ── */

export function KitStatusPill({ jobId }: { jobId: string }) {
  const { materialKits } = useAppData();
  const kit = materialKits.find((k) => k.jobId === jobId);

  if (!kit) {
    return (
      <div className="rounded-lg border-2 border-destructive/30 bg-destructive/5 p-3">
        <div className="flex items-center gap-2 text-destructive font-semibold text-sm">
          <AlertTriangle className="h-4 w-4" /> Material Kit not generated
        </div>
        <p className="text-[11px] text-destructive/80 mt-1">Do not begin install.</p>
      </div>
    );
  }
  if (kit.override) {
    return (
      <div className="rounded-lg border-2 border-warning/40 bg-warning/5 p-3">
        <div className="flex items-center gap-2 text-warning font-semibold text-sm">
          <AlertTriangle className="h-4 w-4" /> Override approved
        </div>
        <p className="text-[11px] text-warning/90 mt-1">
          PM override on file — reason:{" "}
          <span className="font-medium">{kit.override.reason.replace(/_/g, " ")}</span>.
          Proceed per PM direction.
        </p>
      </div>
    );
  }
  if (isKitGateCleared(kit)) {
    const p = kitProgress(kit);
    return (
      <div className="rounded-lg border-2 border-success/30 bg-success/5 p-3">
        <div className="flex items-center gap-2 text-success font-semibold text-sm">
          <CheckCircle2 className="h-4 w-4" /> Material Kit confirmed
        </div>
        <p className="text-[11px] text-success/80 mt-1">
          {p.confirmed} of {p.total} items confirmed.
        </p>
      </div>
    );
  }
  const p = kitProgress(kit);
  return (
    <div className="rounded-lg border-2 border-destructive/30 bg-destructive/5 p-3">
      <div className="flex items-center gap-2 text-destructive font-semibold text-sm">
        <AlertTriangle className="h-4 w-4" /> Material Kit incomplete
      </div>
      <p className="text-[11px] text-destructive/80 mt-1">
        {p.confirmed} of {p.total} confirmed. Do not begin install.
      </p>
    </div>
  );
}

/* ── PhaseSection — single phase card ── */

type PhaseSectionProps = {
  specPhase: JobSpecPhase;
  exec?: PhaseExecution;
  isActive: boolean;
  unlocked: boolean;
  ready: boolean;
  canInteract: boolean;
  onCapturePhoto: (photoType: RequiredPhotoType) => void;
  onToggleChecklist: (itemId: string, checked: boolean) => void;
  onSubmit: () => void;
};

const PhaseSection = forwardRef<HTMLDivElement, PhaseSectionProps>(({ 
  specPhase,
  exec,
  isActive,
  unlocked,
  ready,
  canInteract,
  onCapturePhoto,
  onToggleChecklist,
  onSubmit,
}, ref) => {
  const status = exec?.status ?? "not_started";
  const pct = exec ? phaseProgressPct(exec, specPhase) : 0;
  const terminal = status === "submitted" || status === "qa_passed";
  const interactive = canInteract && unlocked && !terminal;

  const statusBadge = (() => {
    switch (status) {
      case "qa_passed":
        return <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/20">QA Passed</Badge>;
      case "qa_failed":
        return <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20">Returned by QA</Badge>;
      case "submitted":
        return <Badge variant="outline" className="text-[10px] bg-info/10 text-info border-info/20">Awaiting QA</Badge>;
      case "in_progress":
        return <Badge variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/20">In Progress</Badge>;
      default:
        return <Badge variant="outline" className="text-[10px]">Not Started</Badge>;
    }
  })();

  const failReason = getActiveQaFailReason(exec);

  return (
    <div
      ref={ref}
      className={`rounded-xl border-2 p-3.5 ${
        isActive ? "border-primary/40 bg-primary/[0.03]" : "border-border bg-card"
      } ${!unlocked ? "opacity-60" : ""}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-bold">{specPhase.label}</span>
          {isActive && (
            <Badge variant="outline" className="text-[9px] bg-primary/10 text-primary border-primary/20">
              ACTIVE
            </Badge>
          )}
        </div>
        {statusBadge}
      </div>

      {/* progress */}
      <div className="mb-2.5">
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full rounded-full ${status === "qa_passed" ? "bg-success" : "bg-primary"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">
          {pct}% · {exec?.photos.length ?? 0}/{specPhase.requiredPhotos.length} photos ·{" "}
          {exec?.checklist.filter((c) => c.checked).length ?? 0}/{specPhase.checklistItems.length} checks
        </p>
      </div>

      {/* QA feedback — current state only (tombstone rule) */}
      {failReason && (
        <div className="mb-3 rounded-lg border-2 border-destructive/30 bg-destructive/5 p-2.5">
          <div className="flex items-center gap-1.5 text-destructive font-semibold text-xs mb-1">
            <AlertTriangle className="h-3.5 w-3.5" /> Correction needed
          </div>
          <p className="text-[11px] text-destructive/90 leading-snug">{failReason}</p>
        </div>
      )}

      {/* Required photos */}
      <div className="mb-3">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
          <Camera className="h-3 w-3" /> Required Photos
        </p>
        <div className="grid grid-cols-2 gap-1.5">
          {specPhase.requiredPhotos.map((pt) => {
            const captured = exec?.photos.some((p) => p.photoType === pt);
            return (
              <label
                key={pt}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[10px] border transition-colors ${
                  captured
                    ? "border-success/30 bg-success/5 text-success"
                    : interactive
                    ? "border-border bg-background text-foreground hover:bg-muted/50 cursor-pointer active:scale-[0.98]"
                    : "border-border bg-muted/30 text-muted-foreground cursor-not-allowed"
                }`}
              >
                {captured ? <CheckCircle2 className="h-3 w-3 shrink-0" /> : <Camera className="h-3 w-3 shrink-0" />}
                <span className="truncate">{pt.replace(/_/g, " ")}</span>
                {/* Stub mode: file is not stored. Triggers mobile camera UI for behavioral realism. */}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="sr-only"
                  disabled={!interactive}
                  onChange={(e) => {
                    if (!interactive) return;
                    if (!e.target.files || e.target.files.length === 0) return;
                    onCapturePhoto(pt);
                    e.target.value = "";
                  }}
                />
              </label>
            );
          })}
        </div>
      </div>

      {/* Checklist */}
      <div className="mb-3">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
          <ListChecks className="h-3 w-3" /> Checklist
        </p>
        <ul className="space-y-1">
          {specPhase.checklistItems.map((item) => {
            const c = exec?.checklist.find((x) => x.itemId === item.id);
            const checked = !!c?.checked;
            return (
              <li key={item.id}>
                <label
                  className={`flex items-start gap-2 text-[11px] py-1 ${
                    interactive ? "cursor-pointer" : "cursor-not-allowed"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={checked}
                    disabled={!interactive}
                    onChange={(e) => onToggleChecklist(item.id, e.target.checked)}
                  />
                  <span
                    className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                      checked
                        ? "bg-success border-success text-success-foreground"
                        : interactive
                        ? "border-muted-foreground/40 bg-background"
                        : "border-muted-foreground/20 bg-muted/40"
                    }`}
                  >
                    {checked && <CheckCircle2 className="h-3 w-3" />}
                  </span>
                  <span className={checked ? "text-foreground" : "text-muted-foreground"}>
                    {item.label}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Submit phase */}
      {!terminal && (
        <div className="pt-1 space-y-1.5">
          <Button
            className="w-full"
            size="sm"
            disabled={!interactive || !ready}
            onClick={onSubmit}
          >
            Submit Phase
          </Button>
          <p className="text-[10px] text-muted-foreground text-center leading-snug">
            {!canInteract
              ? "Read-only — this job isn't assigned to you."
              : !unlocked
              ? specPhase.name === "install"
                ? "Locked — confirm Material Kit (or log PM override) first."
                : "Locked — prior phases must pass QA first."
              : !ready
              ? "Complete all photos and checks to submit."
              : "Ready to submit for QA."}
          </p>
        </div>
      )}
    </div>
  );
});

PhaseSection.displayName = "PhaseSection";

/* ── PhaseExecutionPanel — interactive list of all phases for a job ── */

export function PhaseExecutionPanel({
  job,
  spec,
  showKitPanel = true,
  activePhaseName,
}: {
  job: Job;
  spec: JobSpec;
  /** When true, render Material Kit status above the phases list. */
  showKitPanel?: boolean;
  /** Optional override for which phase is highlighted ACTIVE. */
  activePhaseName?: JobSpecPhaseName;
}) {
  const { specExecutions, setSpecExecutions, materialKits } = useAppData();
  const { drafts: orderDrafts } = useOrderDraft();
  const { purchaseOrders } = useVendorPO();
  const { role } = useRole();
  const { matchKey, displayName } = useCurrentContractor();
  const exec = specExecutions.find((e) => e.jobId === job.id);
  const kit = materialKits.find((k) => k.jobId === job.id);

  // Material alignment — same predicate the dispatch gate uses.
  // When alignment fails we replace the work surface entirely with a
  // read-only block card. Mirrors InstallerDashboard behavior.
  const alignment = useMemo(
    () => assertMaterialAlignment(job, { kit, orderDrafts, purchaseOrders }),
    [job, kit, orderDrafts, purchaseOrders],
  );
  const aligned = alignment.ok;

  // Assignment guard — installer can only act on their own jobs.
  const installerMatchKey = role === "installer" ? job.assignedCrew : matchKey;
  const canInteract =
    job.assignedSubcontractor === installerMatchKey || job.assignedCrew === installerMatchKey;

  function withEnsuredExecution(
    apply: (execs: JobSpecExecution[], current: JobSpecExecution) => JobSpecExecution[],
  ) {
    if (!job.boundJobSpec) {
      toast.error("This job has no JobSpec bound. Contact your PM.");
      return;
    }
    setSpecExecutions((prev) => {
      const ensured = ensureExecution(prev, job.id, job.boundJobSpec!);
      const current = ensured.execution;
      return apply(ensured.executions, current);
    });
  }

  function handleCapturePhoto(phaseName: JobSpecPhaseName, photoType: RequiredPhotoType) {
    if (!canInteract) {
      toast.error("This job isn't assigned to you.");
      return;
    }
    withEnsuredExecution((execs, current) =>
      execs.map((e) => (e.jobId === job.id ? recordPhoto(current, phaseName, photoType) : e)),
    );
    toast.success(`Captured: ${photoType.replace(/_/g, " ")}`);
  }

  function handleToggleChecklist(phaseName: JobSpecPhaseName, itemId: string, checked: boolean) {
    if (!canInteract) {
      toast.error("This job isn't assigned to you.");
      return;
    }
    withEnsuredExecution((execs, current) =>
      execs.map((e) => (e.jobId === job.id ? toggleChecklist(current, phaseName, itemId, checked) : e)),
    );
  }

  function handleSubmitPhase(phaseName: JobSpecPhaseName) {
    if (!canInteract) {
      toast.error("This job isn't assigned to you.");
      return;
    }
    if (!job.boundJobSpec) {
      toast.error("This job has no JobSpec bound. Contact your PM.");
      return;
    }
    setSpecExecutions((prev) => {
      const ensured = ensureExecution(prev, job.id, job.boundJobSpec!);
      const result = submitPhase(ensured.execution, spec, phaseName, displayName, new Date(), kit, aligned);
      if (result.ok === true) {
        toast.success(`${phaseName} submitted for QA`);
        const updatedExec = result.execution;
        return ensured.executions.map((e) => (e.jobId === job.id ? updatedExec : e));
      } else {
        toast.error(result.reason);
        return ensured.executions;
      }
    });
  }

  // Change Order halt — takes precedence over material alignment.
  // If a CO is Draft / Pending Review, replace the entire surface.
  const coHalt = useMemo(() => assertChangeOrderHalt(job), [job]);
  if (!coHalt.ok) {
    return <ChangeOrderHaltCard verdict={coHalt} jobId={job.id} moduleName="Contractor Portal" />;
  }

  // Alignment block — read-only, replaces the entire execution surface.
  // Mirrors InstallerDashboard. No continue, no override, no inline action.
  if (!aligned) {
    return <AlignmentBlockCard verdict={alignment} jobId={job.id} moduleName="Contractor Portal" />;
  }

  return (
    <div className="space-y-3">
      {showKitPanel && (
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
            <Package className="h-3 w-3" /> Material Kit
          </p>
          <KitStatusPill jobId={job.id} />
        </div>
      )}
      <div className="space-y-2.5">
        {spec.phases.map((sp) => {
          const pe = exec?.phases.find((p) => p.phaseName === sp.name);
          const unlocked = exec
            ? isPhaseUnlocked(exec, spec, sp.name, kit, aligned)
            : sp === spec.phases[0];
          const ready = pe ? isPhaseReadyToSubmit(pe, sp) : false;
          return (
            <PhaseSection
              key={sp.name}
              specPhase={sp}
              exec={pe}
              isActive={sp.name === activePhaseName}
              unlocked={unlocked}
              ready={ready}
              canInteract={canInteract}
              onCapturePhoto={(pt) => handleCapturePhoto(sp.name, pt)}
              onToggleChecklist={(itemId, checked) => handleToggleChecklist(sp.name, itemId, checked)}
              onSubmit={() => handleSubmitPhase(sp.name)}
            />
          );
        })}
      </div>
    </div>
  );
}
